import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const PreliminaryInfoForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    // Company info is pre-filled
    company: 'Topsail roofing, Inc',
    contact: '',
    phone: '(949) 370-8612',
    
    // Job information
    jobName: '',
    siteAddress: '',
    cityStateZip: '',
    projectValue: '',
    
    // General contractor
    gcName: '',
    gcAddress: '',
    gcProjectManager: '',
    gcPhone: '',
    
    // Property owner
    ownerName: '',
    ownerAddress: '',
    ownerCityStateZip: '',
    
    // Bank and lender
    bankName: '',
    bankAddress: '',
    bankCityStateZip: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'preliminaryInfo') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          // Get project data
          const projectData = await fetchProjectData(projectId);
          console.log('Project data for preliminary info form:', projectData);
          
          // Handle the case where the project is nested in a 'project' property
          const actualProject = projectData.project || projectData;
          
          // Prefill the form data
          const prefilled = { ...formData };
          
          // Prefill job name
          if (actualProject.name) {
            prefilled.jobName = actualProject.name;
          }
          
          // Prefill site address from project data - prioritize site contact address
          if (actualProject.siteContact && actualProject.siteContact.address) {
            // Use site contact address (job address)
            prefilled.siteAddress = actualProject.siteContact.address.street || '';
            
            // Format city, state, zip
            const cityParts = [];
            if (actualProject.siteContact.address.city) cityParts.push(actualProject.siteContact.address.city);
            if (actualProject.siteContact.address.state) cityParts.push(actualProject.siteContact.address.state);
            if (actualProject.siteContact.address.zipCode) cityParts.push(actualProject.siteContact.address.zipCode);
            
            prefilled.cityStateZip = cityParts.join(', ');
          } else if (actualProject.address) {
            // Fallback to project address if site contact address is not available
            prefilled.siteAddress = actualProject.address.street || '';
            
            // Format city, state, zip
            const cityParts = [];
            if (actualProject.address.city) cityParts.push(actualProject.address.city);
            if (actualProject.address.state) cityParts.push(actualProject.address.state);
            if (actualProject.address.zipCode) cityParts.push(actualProject.address.zipCode);
            
            prefilled.cityStateZip = cityParts.join(', ');
          }
          
          // Prefill owner information from client data
          if (actualProject.client) {
            prefilled.ownerName = actualProject.client.name || '';
            
            // If client has address, use it for owner address
            if (actualProject.client.address) {
              prefilled.ownerAddress = actualProject.client.address.street || '';
              
              // Format city, state, zip
              const cityParts = [];
              if (actualProject.client.address.city) cityParts.push(actualProject.client.address.city);
              if (actualProject.client.address.state) cityParts.push(actualProject.client.address.state);
              if (actualProject.client.address.zipCode) cityParts.push(actualProject.client.address.zipCode);
              
              prefilled.ownerCityStateZip = cityParts.join(', ');
            }
          }
          
          // Don't prefill contact field - leave it empty
          prefilled.contact = '';
          
          console.log('Prefilled preliminary info form data:', prefilled);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling preliminary info form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Preliminary Information Form updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'preliminaryInfo',
          formData
        })).unwrap();
        showToast('Preliminary Information Form saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Preliminary Information Form', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    // Create a printable version of the form content
    const formContent = `
      ${createCompanyHeader()}
      <h1 class="form-title">PRELIMINARY LIEN INFORMATION FORM</h1>
      
      <div class="form-section">
        <h2 class="form-subtitle">COMPANY INFORMATION</h2>
        <table class="form-table">
          <tbody>
            ${formatTableRow('COMPANY', formData.company)}
            ${formatTableRow('CONTACT', formData.contact)}
            ${formatTableRow('Phone #', formData.phone)}
          </tbody>
        </table>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">JOB INFORMATION</h2>
        <table class="form-table">
          <tbody>
            ${formatTableRow('JOB NAME', formData.jobName)}
            ${formatTableRow('SITE ADDRESS', formData.siteAddress)}
            ${formatTableRow('CITY, STATE, ZIP', formData.cityStateZip)}
            ${formatTableRow('PROJECT VALUE: $', formData.projectValue)}
          </tbody>
        </table>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">GENERAL CONTRACTOR</h2>
        <table class="form-table">
          <tbody>
            ${formatTableRow('NAME', formData.gcName)}
            ${formatTableRow('Address: City, State, Zip', formData.gcAddress)}
            ${formatTableRow('PROJECT MANAGER', formData.gcProjectManager)}
            ${formatTableRow('Phone #', formData.gcPhone)}
          </tbody>
        </table>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">PROPERTY OWNER INFORMATION (Required if no General Contractor)</h2>
        <table class="form-table">
          <tbody>
            ${formatTableRow('NAME', formData.ownerName)}
            ${formatTableRow('ADDRESS', formData.ownerAddress)}
            ${formatTableRow('CITY, STATE, ZIP', formData.ownerCityStateZip)}
          </tbody>
        </table>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">BANK AND LENDER INFORMATION</h2>
        <table class="form-table">
          <tbody>
            ${formatTableRow('BANK/LENDER NAME', formData.bankName)}
            ${formatTableRow('ADDRESS', formData.bankAddress)}
            ${formatTableRow('CITY, STATE, ZIP', formData.bankCityStateZip)}
          </tbody>
        </table>
      </div>
    `;
    
    // Print the form
    printPermitForm(formData, 'Preliminary Lien Information Form', formContent);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto relative">
      <h1 className="text-2xl font-bold mb-6 text-center">PRELIMINARY LIEN INFORMATION FORM</h1>

      <form onSubmit={handleSubmit}>
        {/* Company Information */}
        <div className="mb-6">
          <table className="w-full border-collapse border border-gray-300">
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2 font-medium w-1/4">COMPANY:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                    readOnly
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">CONTACT:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="contact"
                    value={formData.contact}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">Phone #:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                    readOnly
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Job Information */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-2">JOB INFORMATION</h2>
          <table className="w-full border-collapse border border-gray-300">
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2 font-medium w-1/4">JOB NAME:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="jobName"
                    value={formData.jobName}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">SITE ADDRESS:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="siteAddress"
                    value={formData.siteAddress}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">CITY, STATE, ZIP:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="cityStateZip"
                    value={formData.cityStateZip}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">PROJECT VALUE: $</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="projectValue"
                    value={formData.projectValue}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* General Contractor */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-2">GENERAL CONTRACTOR</h2>
          <table className="w-full border-collapse border border-gray-300">
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2 font-medium w-1/4">NAME:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="gcName"
                    value={formData.gcName}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">Address: City, State, Zip:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="gcAddress"
                    value={formData.gcAddress}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">PROJECT MANAGER:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="gcProjectManager"
                    value={formData.gcProjectManager}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">Phone #:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="gcPhone"
                    value={formData.gcPhone}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Property Owner */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-2">PROPERTY OWNER INFORMATION (Required if no General Contractor)</h2>
          <table className="w-full border-collapse border border-gray-300">
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2 font-medium w-1/4">NAME:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">ADDRESS:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="ownerAddress"
                    value={formData.ownerAddress}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">CITY, STATE, ZIP</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="ownerCityStateZip"
                    value={formData.ownerCityStateZip}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Bank and Lender */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-2">BANK AND LENDER INFORMATION</h2>
          <table className="w-full border-collapse border border-gray-300">
            <tbody>
              <tr>
                <td className="border border-gray-300 p-2 font-medium w-1/4">BANK/LENDER NAME:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">ADDRESS:</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="bankAddress"
                    value={formData.bankAddress}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 p-2 font-medium">CITY, STATE, ZIP</td>
                <td className="border border-gray-300 p-2">
                  <input
                    type="text"
                    name="bankCityStateZip"
                    value={formData.bankCityStateZip}
                    onChange={handleChange}
                    className="w-full p-1 border border-gray-300"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default PreliminaryInfoForm;
